export interface LatLngCoords {
  lat: number;
  lng: number;
}

export interface GoogleSearchSuggestion {
  lat: number;
  lng: number;
  title: string;
  subtitle?: string;
}

/**
 * Parses pasted/typed coordinate text like:
 * "12.9716, 77.5946" | "12.9716 77.5946" | "12.9716;77.5946"
 */
export function parseLatLng(text: string): LatLngCoords | null {
  if (!text) {
    return null;
  }
  const match = text.trim().match(
    /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/
  );
  if (!match) {
    return null;
  }
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  return { lat, lng };
}

function readAutocompleteInputValue(autocompleteElement: HTMLElement): string {
  const anyEl = autocompleteElement as any;
  if (typeof anyEl.value === 'string' && anyEl.value.trim()) {
    return anyEl.value;
  }
  const shadowInput = autocompleteElement.shadowRoot?.querySelector('input') as HTMLInputElement | null;
  if (shadowInput?.value) {
    return shadowInput.value;
  }
  const lightInput = autocompleteElement.querySelector('input') as HTMLInputElement | null;
  return lightInput?.value || '';
}

function findInnerSearchInput(autocompleteElement: HTMLElement): HTMLInputElement | null {
  return (
    (autocompleteElement.shadowRoot?.querySelector('input') as HTMLInputElement | null) ||
    (autocompleteElement.querySelector('input') as HTMLInputElement | null)
  );
}

function formatCoordsLabel(coords: LatLngCoords): string {
  return `${coords.lat}, ${coords.lng}`;
}

function applyDropdownBaseStyles(el: HTMLElement): void {
  Object.assign(el.style, {
    position: 'absolute',
    left: '0',
    right: '0',
    top: '100%',
    marginTop: '4px',
    zIndex: '10000',
    background: '#fff',
    border: '1px solid #dadce0',
    borderRadius: '4px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    maxHeight: '220px',
    overflowY: 'auto'
  } as CSSStyleDeclaration);
}

function applyItemBaseStyles(item: HTMLButtonElement): void {
  Object.assign(item.style, {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: '0',
    background: 'transparent',
    padding: '10px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    lineHeight: '1.35',
    color: '#202124'
  } as CSSStyleDeclaration);
}

function getGoogleMaps(): any {
  return (window as any).google?.maps;
}

const DEFAULT_SEARCH_PLACEHOLDER = 'Search Address';

/**
 * Configures PlaceAutocompleteElement for a clean single-bar map search UI
 * and wires lat/lng + full-address paste support.
 */
export function configureGoogleMapSearchAutocomplete(
  autocompleteElement: any,
  onCoordinates: (coords: LatLngCoords) => void,
  placeholder: string = DEFAULT_SEARCH_PLACEHOLDER
): HTMLElement {
  if (!autocompleteElement) {
    return autocompleteElement;
  }

  try {
    autocompleteElement.includedRegionCodes = ['IN'];
  } catch {
    // older builds may only accept constructor options
  }
  try {
    autocompleteElement.noClearButton = true;
  } catch { /* ignore */ }
  try {
    autocompleteElement.noInputIcon = true;
  } catch { /* ignore */ }
  try {
    autocompleteElement.placeholder = placeholder;
  } catch { /* ignore */ }

  autocompleteElement.setAttribute?.('no-clear-button', '');
  autocompleteElement.setAttribute?.('no-input-icon', '');
  autocompleteElement.setAttribute?.('placeholder', placeholder);
  autocompleteElement.classList?.add?.('map-search-input');
  autocompleteElement.style.width = '100%';
  autocompleteElement.style.display = 'block';

  bindGoogleLatLngSearchSupport(autocompleteElement, onCoordinates);
  return autocompleteElement;
}

/**
 * Free-text / lat-lng support under PlaceAutocompleteElement.
 * Places Autocomplete often returns nothing for full pasted addresses or coordinates.
 * This shows a custom dropdown; map moves only after the user selects an option.
 */
export function bindGoogleLatLngSearchSupport(
  autocompleteElement: HTMLElement,
  onCoordinates: (coords: LatLngCoords) => void
): void {
  if (!autocompleteElement || typeof onCoordinates !== 'function') {
    return;
  }

  const host = (autocompleteElement.parentElement || autocompleteElement) as HTMLElement;
  const hostPosition = window.getComputedStyle(host).position;
  if (hostPosition === 'static' || !hostPosition) {
    host.style.position = 'relative';
  }

  let suggestions: GoogleSearchSuggestion[] = [];
  let highlightedIndex = 0;
  let dropdownEl: HTMLElement | null = null;
  let requestId = 0;
  let addressDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  const clearAddressDebounce = () => {
    if (addressDebounceTimer != null) {
      clearTimeout(addressDebounceTimer);
      addressDebounceTimer = null;
    }
  };

  const removeDropdown = () => {
    clearAddressDebounce();
    suggestions = [];
    highlightedIndex = 0;
    requestId += 1;
    if (dropdownEl?.parentElement) {
      dropdownEl.parentElement.removeChild(dropdownEl);
    }
    dropdownEl = null;
  };

  const selectSuggestion = (index: number): boolean => {
    const suggestion = suggestions[index];
    if (!suggestion) {
      return false;
    }
    const coords = { lat: suggestion.lat, lng: suggestion.lng };
    removeDropdown();
    onCoordinates(coords);
    return true;
  };

  const selectHighlighted = (): boolean => {
    if (!suggestions.length) {
      return false;
    }
    return selectSuggestion(Math.max(0, Math.min(highlightedIndex, suggestions.length - 1)));
  };

  const renderDropdown = (items: GoogleSearchSuggestion[], emptyMessage?: string) => {
    suggestions = items;
    highlightedIndex = 0;
    if (dropdownEl?.parentElement) {
      dropdownEl.parentElement.removeChild(dropdownEl);
    }

    dropdownEl = document.createElement('div');
    dropdownEl.className = 'google-latlng-dropdown';
    dropdownEl.setAttribute('role', 'listbox');
    applyDropdownBaseStyles(dropdownEl);

    if (!items.length) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        padding: '10px 12px',
        fontSize: '13px',
        color: '#5f6368'
      } as CSSStyleDeclaration);
      empty.textContent = emptyMessage || 'No results found';
      dropdownEl.appendChild(empty);
      host.appendChild(dropdownEl);
      return;
    }

    items.forEach((suggestion, index) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'google-latlng-dropdown__item';
      item.setAttribute('role', 'option');
      applyItemBaseStyles(item);

      const title = document.createElement('div');
      title.className = 'google-latlng-dropdown__title';
      title.textContent = suggestion.title;

      item.appendChild(title);
      if (suggestion.subtitle) {
        const subtitle = document.createElement('div');
        subtitle.className = 'google-latlng-dropdown__subtitle';
        Object.assign(subtitle.style, {
          marginTop: '2px',
          fontSize: '11px',
          color: '#5f6368'
        } as CSSStyleDeclaration);
        subtitle.textContent = suggestion.subtitle;
        item.appendChild(subtitle);
      }

      const setHighlight = (active: boolean) => {
        item.style.background = active ? '#f1f3f4' : 'transparent';
      };
      if (index === highlightedIndex) {
        setHighlight(true);
      }

      item.addEventListener('mouseenter', () => {
        highlightedIndex = index;
        Array.from(dropdownEl?.querySelectorAll('button') || []).forEach((btn, i) => {
          (btn as HTMLElement).style.background = i === index ? '#f1f3f4' : 'transparent';
        });
      });
      item.addEventListener('mouseleave', () => {
        if (highlightedIndex !== index) {
          setHighlight(false);
        }
      });
      item.addEventListener('mousedown', event => {
        event.preventDefault();
      });
      item.addEventListener('click', () => {
        selectSuggestion(index);
      });

      dropdownEl.appendChild(item);
    });

    host.appendChild(dropdownEl);
  };

  const showCoordsSuggestion = (coords: LatLngCoords) => {
    clearAddressDebounce();
    const suggestion: GoogleSearchSuggestion = {
      lat: coords.lat,
      lng: coords.lng,
      title: formatCoordsLabel(coords),
      subtitle: 'Coordinates'
    };
    renderDropdown([suggestion]);

    const maps = getGoogleMaps();
    if (!maps?.Geocoder) {
      return;
    }
    const currentRequest = ++requestId;
    const geocoder = new maps.Geocoder();
    geocoder.geocode({ location: { lat: coords.lat, lng: coords.lng } }, (results: any[], status: string) => {
      if (currentRequest !== requestId || !dropdownEl || !suggestions.length) {
        return;
      }
      if (status === 'OK' && results?.[0]?.formatted_address) {
        suggestions[0] = {
          ...suggestions[0],
          title: results[0].formatted_address,
          subtitle: formatCoordsLabel(coords)
        };
        renderDropdown(suggestions);
      }
    });
  };

  const geocodeAddress = (query: string) => {
    const trimmed = (query || '').trim();
    if (!trimmed || parseLatLng(trimmed)) {
      return;
    }

    const maps = getGoogleMaps();
    if (!maps?.Geocoder) {
      renderDropdown([], 'Geocoder unavailable');
      return;
    }

    const currentRequest = ++requestId;
    // Lightweight loading state
    renderDropdown([], 'Searching…');

    const geocoder = new maps.Geocoder();
    geocoder.geocode(
      {
        address: trimmed,
        componentRestrictions: { country: 'IN' }
      },
      (results: any[], status: string) => {
        if (currentRequest !== requestId) {
          return;
        }
        if (status !== 'OK' || !results?.length) {
          renderDropdown([], 'No results found');
          return;
        }
        const items: GoogleSearchSuggestion[] = results.slice(0, 5).map(result => {
          const location = result.geometry?.location;
          const lat = typeof location?.lat === 'function' ? location.lat() : Number(location?.lat);
          const lng = typeof location?.lng === 'function' ? location.lng() : Number(location?.lng);
          return {
            lat,
            lng,
            title: result.formatted_address || trimmed,
            subtitle: formatCoordsLabel({ lat, lng })
          };
        }).filter(item => Number.isFinite(item.lat) && Number.isFinite(item.lng));

        if (!items.length) {
          renderDropdown([], 'No results found');
          return;
        }
        renderDropdown(items);
      }
    );
  };

  const handleQueryText = (raw: string, options?: { geocodeAddressIfNeeded?: boolean }) => {
    const text = (raw || '').trim();
    if (!text) {
      removeDropdown();
      return false;
    }

    const coords = parseLatLng(text);
    if (coords) {
      showCoordsSuggestion(coords);
      return true;
    }

    if (options?.geocodeAddressIfNeeded) {
      geocodeAddress(text);
      return true;
    }

    // Typing short place names: leave Places Autocomplete alone; clear our custom list.
    removeDropdown();
    return false;
  };

  const onPaste = (event: ClipboardEvent) => {
    const text = (event.clipboardData?.getData('text') || '').trim();
    if (!text) {
      return;
    }
    setTimeout(() => {
      const value = text || readAutocompleteInputValue(autocompleteElement);
      // Paste of coords or full address → custom dropdown (Places often returns nothing).
      handleQueryText(value, { geocodeAddressIfNeeded: true });
    }, 0);
  };

  const onInput = () => {
    const value = readAutocompleteInputValue(autocompleteElement).trim();
    if (!value) {
      removeDropdown();
      return;
    }
    if (parseLatLng(value)) {
      showCoordsSuggestion(parseLatLng(value)!);
      return;
    }
    // Debounced geocode for long/full addresses (comma-separated or long paste-like text).
    // Short queries stay with Places Autocomplete.
    clearAddressDebounce();
    const looksLikeFullAddress = value.includes(',') || value.length >= 40;
    if (!looksLikeFullAddress) {
      removeDropdown();
      return;
    }
    addressDebounceTimer = setTimeout(() => {
      geocodeAddress(value);
    }, 400);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (dropdownEl) {
        removeDropdown();
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (event.key === 'ArrowDown' && suggestions.length) {
      event.preventDefault();
      highlightedIndex = (highlightedIndex + 1) % suggestions.length;
      renderDropdown(suggestions);
      return;
    }
    if (event.key === 'ArrowUp' && suggestions.length) {
      event.preventDefault();
      highlightedIndex = (highlightedIndex - 1 + suggestions.length) % suggestions.length;
      renderDropdown(suggestions);
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // Confirm visible suggestion only — never silent jump.
    if (dropdownEl && suggestions.length) {
      if (selectHighlighted()) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    const value = readAutocompleteInputValue(autocompleteElement).trim();
    if (!value) {
      return;
    }
    // Show dropdown for coords or geocoded address; user selects next.
    handleQueryText(value, { geocodeAddressIfNeeded: true });
    event.preventDefault();
    event.stopPropagation();
  };

  const onDocumentPointerDown = (event: Event) => {
    if (!dropdownEl) {
      return;
    }
    const target = event.target as Node | null;
    if (target && (dropdownEl.contains(target) || autocompleteElement.contains(target) || host.contains(target))) {
      return;
    }
    const path = typeof (event as any).composedPath === 'function' ? (event as any).composedPath() : [];
    if (path.includes(dropdownEl) || path.includes(autocompleteElement) || path.includes(host)) {
      return;
    }
    removeDropdown();
  };

  document.addEventListener('mousedown', onDocumentPointerDown, true);

  const attachToInput = (attempt = 0) => {
    const input = findInnerSearchInput(autocompleteElement);
    if (!input) {
      if (attempt < 20) {
        setTimeout(() => attachToInput(attempt + 1), 50);
      }
      autocompleteElement.addEventListener('paste', onPaste);
      autocompleteElement.addEventListener('input', onInput);
      autocompleteElement.addEventListener('keydown', onKeyDown);
      return;
    }
    input.addEventListener('paste', onPaste);
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeyDown);
  };

  attachToInput();
}
